// Configurações da Plataforma
import { useState, useEffect } from 'react';
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import { Input } from '../../../components/common/Input';
import { Select } from '../../../components/common/Select';
import { Breadcrumbs } from '../../../components/layout/Breadcrumbs';
import { Loading } from '../../../components/common/Loading';
import { AlertCircle, Save, Settings, Plug, Globe } from 'lucide-react';
import { getConfiguracoesAsObject, updateConfiguracoes } from '../../../services/configuracoesService';

export function AdminConfiguracoes() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [config, setConfig] = useState({
    nomePlataforma: 'proNotas',
    emailContato: 'contato@pronotas.com',
    telefoneContato: '(11) 98765-4321',
    urlApi: 'https://api.pronotas.com',
    urlWebhook: 'https://webhook.pronotas.com',
    integracaoSefaz: false,
    integracaoPrefeitura: false,
    ambiente: 'produção',
    modoManutencao: false,
  });

  useEffect(() => {
    carregarConfiguracoes();
  }, []);

  const carregarConfiguracoes = async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar configurações do banco de dados
      const configuracoes = await getConfiguracoesAsObject();

      setConfig({
        nomePlataforma: configuracoes.nome_plataforma || 'proNotas',
        emailContato: configuracoes.email_contato || 'contato@pronotas.com',
        telefoneContato: configuracoes.telefone_contato || '(11) 98765-4321',
        urlApi: configuracoes.url_api || 'https://api.pronotas.com',
        urlWebhook: configuracoes.url_webhook || 'https://webhook.pronotas.com',
        integracaoSefaz: configuracoes.integracao_sefaz || false,
        integracaoPrefeitura: configuracoes.integracao_prefeitura || false,
        ambiente: configuracoes.ambiente || 'produção',
        modoManutencao: configuracoes.modo_manutencao || false,
      });
    } catch (err: any) {
      console.error('[AdminConfiguracoes] Erro ao carregar configurações:', err);
      setError(err.message || 'Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Salvar configurações no banco de dados
      await updateConfiguracoes({
        nome_plataforma: config.nomePlataforma,
        email_contato: config.emailContato,
        telefone_contato: config.telefoneContato,
        url_api: config.urlApi,
        url_webhook: config.urlWebhook,
        integracao_sefaz: String(config.integracaoSefaz),
        integracao_prefeitura: String(config.integracaoPrefeitura),
        ambiente: config.ambiente,
        modo_manutencao: String(config.modoManutencao),
      });

      setSuccess('Configurações salvas com sucesso!');
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'Admin', path: '/app/admin' }, { label: 'Configurações' }]} />
        <div className="flex items-center justify-center py-12">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: 'Admin', path: '/app/admin' }, { label: 'Configurações' }]} />

      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Configurações da Plataforma</h1>
        <p className="text-gray-600 dark:text-gray-400">Configure as integrações e definições gerais da plataforma</p>
      </div>

      {error && (
        <Card>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {success && (
        <Card>
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-2 text-green-700 dark:text-green-400">
            <AlertCircle className="w-5 h-5" />
            <span>{success}</span>
          </div>
        </Card>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Informações Gerais */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Informações Gerais</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nome da Plataforma"
              value={config.nomePlataforma}
              onChange={(e) => setConfig({ ...config, nomePlataforma: e.target.value })}
              required
            />
            <Input
              label="Email de Contato"
              type="email"
              value={config.emailContato}
              onChange={(e) => setConfig({ ...config, emailContato: e.target.value })}
              required
            />
            <Input
              label="Telefone de Contato"
              value={config.telefoneContato}
              onChange={(e) => setConfig({ ...config, telefoneContato: e.target.value })}
              required
            />
            <Select
              label="Ambiente"
              value={config.ambiente}
              onChange={(e) => setConfig({ ...config, ambiente: e.target.value })}
              options={[
                { value: 'desenvolvimento', label: 'Desenvolvimento' },
                { value: 'homologacao', label: 'Homologação' },
                { value: 'producao', label: 'Produção' },
              ]}
            />
          </div>
        </Card>

        {/* Integrações */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Plug className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Integrações</h2>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="URL da API"
                value={config.urlApi}
                onChange={(e) => setConfig({ ...config, urlApi: e.target.value })}
                placeholder="https://api.exemplo.com"
              />
              <Input
                label="URL do Webhook"
                value={config.urlWebhook}
                onChange={(e) => setConfig({ ...config, urlWebhook: e.target.value })}
                placeholder="https://webhook.exemplo.com"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="integracaoSefaz"
                  checked={config.integracaoSefaz}
                  onChange={(e) => setConfig({ ...config, integracaoSefaz: e.target.checked })}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <label htmlFor="integracaoSefaz" className="text-sm text-gray-700 dark:text-gray-300">
                  Integração com SEFAZ
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="integracaoPrefeitura"
                  checked={config.integracaoPrefeitura}
                  onChange={(e) => setConfig({ ...config, integracaoPrefeitura: e.target.checked })}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <label htmlFor="integracaoPrefeitura" className="text-sm text-gray-700 dark:text-gray-300">
                  Integração com Prefeitura
                </label>
              </div>
            </div>
          </div>
        </Card>

        {/* Manutenção */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Settings className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Manutenção</h2>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="modoManutencao"
              checked={config.modoManutencao}
              onChange={(e) => setConfig({ ...config, modoManutencao: e.target.checked })}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <label htmlFor="modoManutencao" className="text-sm text-gray-700 dark:text-gray-300">
              Ativar modo de manutenção (bloqueia acesso de usuários)
            </label>
          </div>
        </Card>

        {/* Botões */}
        <div className="flex gap-4 justify-end">
          <Button type="submit" variant="primary" disabled={saving} isLoading={saving}>
            <Save className="w-4 h-4 mr-2" />
            Salvar Configurações
          </Button>
        </div>
      </form>
    </div>
  );
}

